import { Search, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export type AnchorSortBy = 'updated' | 'created' | 'alpha';

export interface AnchorsFilterBarProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  domainFilter: string;
  onDomainFilterChange: (domain: string) => void;
  contactFilter: string;
  onContactFilterChange: (contact: string) => void;
  sortBy: AnchorSortBy;
  onSortByChange: (sort: AnchorSortBy) => void;
  domains: string[];
  contacts?: { id: number; label: string }[];
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

export function AnchorsFilterBar({
  searchQuery,
  onSearchQueryChange,
  domainFilter,
  onDomainFilterChange,
  contactFilter,
  onContactFilterChange,
  sortBy,
  onSortByChange,
  domains,
  contacts = [],
  hasActiveFilters,
  onResetFilters,
}: AnchorsFilterBarProps) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center w-full">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search anchors by label, domain, or URL..."
          className="pl-10 bg-card"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
        />
      </div>

      <Select value={domainFilter} onValueChange={onDomainFilterChange}>
        <SelectTrigger className="w-full md:w-[180px] bg-card">
          <SelectValue placeholder="All Domains" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Domains</SelectItem>
          {domains.map((domain) => (
            <SelectItem key={domain} value={domain}>
              {domain}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={contactFilter} onValueChange={onContactFilterChange}>
        <SelectTrigger className="w-full md:w-[180px] bg-card">
          <SelectValue placeholder="All Contacts" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Contacts</SelectItem>
          <SelectItem value="linked">Any Linked Contact</SelectItem>
          <SelectItem value="unlinked">Unlinked</SelectItem>
          {contacts.map((contact) => (
            <SelectItem key={contact.id} value={String(contact.id)}>
              {contact.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={sortBy} onValueChange={(val) => onSortByChange(val as AnchorSortBy)}>
        <SelectTrigger className="w-full md:w-[160px] bg-card">
          <SelectValue placeholder="Sort by" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="updated">Recently Updated</SelectItem>
          <SelectItem value="created">Recently Created</SelectItem>
          <SelectItem value="alpha">Alphabetical (A-Z)</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onResetFilters}
          className="text-muted-foreground hover:text-foreground h-9 px-2 text-xs font-medium shrink-0"
        >
          <X className="mr-1 h-3.5 w-3.5" />
          Reset
        </Button>
      )}
    </div>
  );
}
